import React, { useRef } from 'react';
import { Box, Typography, Chip, Accordion, AccordionSummary, AccordionDetails, IconButton } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ListAltIcon from '@mui/icons-material/ListAlt';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TechActionGrid from './TechActionGrid';
const TechActionTimeline = ({ event = [], actionEvents = [], selectedDate, onDateChange, onSelectSchedule }) => {
    
    const dateInputRef = useRef(null);

    const handleHeaderClick = () => {
        if (dateInputRef.current) {
            dateInputRef.current.showPicker();
        }
    };

    // 1. 입력창으로 직접 날짜를 바꿀 때
    const handleDateChange = (e) => {
        if (e.target.value) {
            const newDate = new Date(e.target.value);
            onDateChange(newDate, 'reset'); // 👈 날짜와 'reset' 변수를 함께 토스
        }
    };

    // 2. 이전 날짜 버튼을 누를 때
    const handlePrevDay = () => {
        const prev = new Date(selectedDate);
        prev.setDate(prev.getDate() - 1);
        onDateChange(prev, 'reset'); // 👈 날짜와 'reset' 변수를 함께 토스
    };

    // 3. 다음 날짜 버튼을 누를 때
    const handleNextDay = () => {
        const next = new Date(selectedDate);
        next.setDate(next.getDate() + 1);
        onDateChange(next, 'reset'); // 👈 날짜와 'reset' 변수를 함께 토스
    };

    const generateTimeSlots = () => {
        const slots = [];
        for (let hour = 0; hour <= 23; hour++) {
            const padHour = String(hour).padStart(2, '0');
            slots.push(`${padHour}:00`);
            slots.push(`${padHour}:30`);
        }
        return slots;
    };

    const timeSlots = generateTimeSlots();
    const daysOfWeek = ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일'];

    const formattedDate = `${selectedDate.getMonth() + 1}월 ${selectedDate.getDate()}일`;
    const formattedDay = daysOfWeek[selectedDate.getDay()];

    const getInputDateFormat = (date) => {
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };

    const formatDateLabel = (dateStr) => {
        const d = new Date(dateStr);
        return `${d.getMonth() + 1}/${d.getDate()}`;
    };

    const today = new Date();
    const isToday = selectedDate.getDate() === today.getDate() && 
                    selectedDate.getMonth() === today.getMonth() && 
                    selectedDate.getFullYear() === today.getFullYear();

    const filteredSchedules = event.filter(evt => {
        const evtStart = new Date(evt.start);
        const evtEnd = new Date(evt.end);
        const targetStart = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 0, 0, 0);
        const targetEnd = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 23, 59, 59);
        return evtStart <= targetEnd && evtEnd >= targetStart;
    }).map(evt => {
        const start = new Date(evt.start);
        const end = new Date(evt.end);
        const isMultiDay = (new Date(end.getFullYear(), end.getMonth(), end.getDate()) - 
                            new Date(start.getFullYear(), start.getMonth(), start.getDate())) >= 24 * 60 * 60 * 1000;
        let dateLabel = isMultiDay ? ` (${formatDateLabel(evt.start)} - ${formatDateLabel(evt.end)})` : ` (${formatDateLabel(evt.start)})`;
        return { ...evt, displayTitle: `${evt.title}${dateLabel}` };
    });

    return (
        <Box 
            sx={{ 
                width: '100%', 
                display: 'flex',
                flexDirection: 'column',
                gap: 2
            }}
        >
            <Box sx={{ pb: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
                <Box 
                    onClick={handleHeaderClick}
                    sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        cursor: 'pointer',
                        p: 0.5,
                        m: -0.5,
                        borderRadius: '6px',
                        transition: 'background-color 0.2s',
                        '&:hover': { backgroundColor: '#f1f3f5' }
                    }}
                >
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#111111', letterSpacing: '-0.5px', display: 'flex', alignItems: 'center', gap: 1 }}>
                        {formattedDate}
                        <Typography component="span" variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                            {formattedDay}
                        </Typography>
                    </Typography>
                    <CalendarTodayIcon sx={{ fontSize: '1.1rem', color: 'text.secondary', ml: 0.5 }} />
                </Box>

                <input 
                    type="date"
                    ref={dateInputRef}
                    value={getInputDateFormat(selectedDate)}
                    onChange={handleDateChange}
                    style={{ position: 'absolute', opacity: 0, width: 0, height: 0, padding: 0, border: 'none' }}
                />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {isToday && (
                        <Chip label="오늘" size="small" sx={{ bgcolor: '#eef2f6', color: '#1e4620', fontWeight: 700, fontSize: '0.75rem', mr: 1 }} />
                    )}
                    <IconButton size="small" onClick={handlePrevDay} sx={{ border: '1px solid #e9ecef', borderRadius: '6px' }}>
                        <ChevronLeftIcon sx={{ fontSize: '1.2rem' }} />
                    </IconButton>
                    <IconButton size="small" onClick={handleNextDay} sx={{ border: '1px solid #e9ecef', borderRadius: '6px' }}>
                        <ChevronRightIcon sx={{ fontSize: '1.2rem' }} />
                    </IconButton>
                </Box>
            </Box>
            <Box sx={{ mb: 1 }}>
                <Accordion 
                    defaultExpanded
                    variant="outlined" 
                    sx={{ 
                        borderRadius: '6px !important', 
                        borderColor: '#e9ecef',
                        boxShadow: 'none',
                        '&:before': { display: 'none' }
                    }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '1.1rem' }} />} sx={{ minHeight: '36px !important', height: 36, px: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ListAltIcon sx={{ fontSize: '1.1rem', color: '#228be6' }} />
                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#495057' }}>
                                기술 지원 일정 등록 사항
                            </Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 1, pt: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {filteredSchedules.length > 0 ? (
                            filteredSchedules.map(s => (
                                <Box 
                                    key={s.id} 
                                    onClick={() => typeof onSelectSchedule === 'function' && onSelectSchedule(s)}
                                    sx={{ 
                                        p: 0.75, 
                                        borderRadius: '4px', 
                                        backgroundColor: '#f8f9fa', 
                                        borderLeft: '3px solid #228be6',
                                        fontSize: '0.775rem', 
                                        cursor: 'pointer', 
                                        transition: 'background-color 0.2s',
                                        '&:hover': { backgroundColor: '#e7f5ff' }
                                    }}
                                >
                                    {s.displayTitle}
                                </Box>
                            ))
                        ) : (
                            <Typography variant="caption" sx={{ color: 'text.secondary', p: 1, textAlign: 'center' }}>
                                해당 일자에 등록된 기술 지원 일정 등록 사항이 없습니다.
                            </Typography>
                        )}
                    </AccordionDetails>
                </Accordion>
            </Box>

            <TechActionGrid 
                timeSlots={timeSlots} 
                actions={actionEvents} 
                selectedDate={selectedDate}
                onSelectSchedule={onSelectSchedule} 
            />
        </Box>
    );
};

export default TechActionTimeline;
